import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { organizations as orgApi } from '@/api/organizations';
import { invites as invitesApi } from '@/api/invites';
import { jira as jiraApi } from '@/api/jira';
import { useRequireOrg } from '@/tenancy/OrgContext';
import { useAuth } from '@/auth/AuthContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatDateTime } from '@/lib/format';
import { GITHUB_APP_INSTALL_URL } from '@/lib/config';
import type { MemberRole } from '@/api/types';

export default function SettingsPage() {
  const { id: orgId, org } = useRequireOrg();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const orgQuery = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => orgApi.get(orgId),
  });
  const membersQuery = useQuery({
    queryKey: ['organization', orgId, 'members'],
    queryFn: () => orgApi.members(orgId),
  });
  const invitesQuery = useQuery({
    queryKey: ['organization', orgId, 'invites'],
    queryFn: () => invitesApi.list(orgId),
  });

  const myRole = user?.organizations.find((o) => o.id === orgId)?.role;
  const canInvite = myRole === 'OWNER' || myRole === 'ADMIN';
  const isOwner = myRole === 'OWNER';

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle={`${org.displayName ?? org.githubAccountLogin}`}
      />

      <section className="card">
        <h3>Organization</h3>
        {orgQuery.isLoading ? <Spinner label="Loading organization…" /> : null}
        {orgQuery.error ? <ErrorBanner error={orgQuery.error} /> : null}
        {orgQuery.data ? (
          <dl className="kv-list">
            <dt>GitHub account</dt>
            <dd>
              @{orgQuery.data.githubAccountLogin}{' '}
              <span className="muted">({orgQuery.data.githubAccountType.toLowerCase()})</span>
            </dd>
            <dt>Account id</dt>
            <dd>{orgQuery.data.githubAccountId}</dd>
            <dt>Current installation</dt>
            <dd>{orgQuery.data.currentInstallationId ?? <span className="muted">— not installed</span>}</dd>
            <dt>Status</dt>
            <dd>{orgQuery.data.status}</dd>
            <dt>Created</dt>
            <dd>{formatDateTime(orgQuery.data.createdAt)}</dd>
          </dl>
        ) : null}
      </section>

      <section className="card">
        <h3>Members</h3>
        {membersQuery.isLoading ? <Spinner label="Loading members…" /> : null}
        {membersQuery.error ? <ErrorBanner error={membersQuery.error} /> : null}
        {membersQuery.data ? (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {membersQuery.data.map(m => (
                  <tr key={m.userId}>
                    <td>
                      {m.name ?? m.email} <span className="muted small">{m.email}</span>
                    </td>
                    <td>{m.role}</td>
                    <td className="muted small">{formatDateTime(m.joinedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {canInvite && (
        <InvitesSection
          orgId={orgId}
          canInvite={canInvite && !!user?.emailVerifiedAt}
          emailVerified={!!user?.emailVerifiedAt}
          invites={invitesQuery.data ?? []}
          loading={invitesQuery.isLoading}
          error={invitesQuery.error}
          onChange={() => queryClient.invalidateQueries({ queryKey: ['organization', orgId, 'invites'] })}
        />
      )}

      <JiraIntegrationSection orgId={orgId} />

      {isOwner && (
        <section className="card">
          <h3>GitHub App</h3>
          <p className="muted">
            To suspend or uninstall this integration, manage it from GitHub. Changes there are
            reflected here automatically via webhooks.
          </p>
          {orgQuery.data?.currentInstallationId ? (
            <a
              className="btn"
              href={
                orgQuery.data.githubAccountType === 'Organization'
                  ? `https://github.com/organizations/${orgQuery.data.githubAccountLogin}/settings/installations/${orgQuery.data.currentInstallationId}`
                  : `https://github.com/settings/installations/${orgQuery.data.currentInstallationId}`
              }
              target="_blank"
              rel="noreferrer"
            >
              Open on GitHub ↗
            </a>
          ) : (
            <a
              className="btn"
              href={GITHUB_APP_INSTALL_URL}
              target="_blank"
              rel="noreferrer"
            >
              Install GitHub App ↗
            </a>
          )}
        </section>
      )}
    </>
  );
}

interface InvitesSectionProps {
  orgId: string;
  canInvite: boolean;
  emailVerified: boolean;
  invites: { id: string; email: string; role: MemberRole; expiresAt: string; createdAt: string }[];
  loading: boolean;
  error: unknown;
  onChange: () => void;
}

function InvitesSection({
  orgId,
  canInvite,
  emailVerified,
  invites,
  loading,
  error,
  onChange,
}: InvitesSectionProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('MEMBER');
  const [formError, setFormError] = useState<unknown>(null);

  const createMutation = useMutation({
    mutationFn: () => invitesApi.create(orgId, email.trim(), role),
    onSuccess: () => {
      setEmail('');
      setFormError(null);
      onChange();
    },
    onError: (err) => setFormError(err),
  });

  const revokeMutation = useMutation({
    mutationFn: (inviteId: string) => invitesApi.revoke(orgId, inviteId),
    onSuccess: () => onChange(),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    createMutation.mutate();
  };

  return (
    <section className="card">
      <h3>Invitations</h3>
      {!emailVerified ? (
        <p className="muted">Verify your email before sending invites.</p>
      ) : (
        <form className="form-grid" onSubmit={handleSubmit}>
          {formError ? <ErrorBanner error={formError} /> : null}
          <label>
            <span>Invite email</span>
            <input
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <span>Role</span>
            <select
              className="select"
              value={role}
              onChange={(e) => setRole(e.target.value as MemberRole)}
            >
              <option value="MEMBER">Member</option>
              <option value="ADMIN">Admin</option>
            </select>
          </label>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canInvite || createMutation.isPending || !email.trim()}
          >
            {createMutation.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </form>
      )}

      <h4 style={{ marginTop: '1.5rem' }}>Pending invites</h4>
      {loading ? <Spinner label="Loading invites…" /> : null}
      {error ? <ErrorBanner error={error} /> : null}
      {invites.length === 0 ? (
        <p className="muted">No pending invites.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td>{inv.role}</td>
                  <td className="muted small">{formatDateTime(inv.expiresAt)}</td>
                  <td>
                    <button
                      className="btn btn-ghost"
                      onClick={() => revokeMutation.mutate(inv.id)}
                      disabled={revokeMutation.isPending}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function JiraIntegrationSection({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionError, setActionError] = useState<unknown>(null);

  const statusQuery = useQuery({
    queryKey: ['jira', 'status', orgId],
    queryFn: () => jiraApi.status(orgId),
  });

  // After the OAuth dance the backend bounces the browser back to /settings with a
  // status flag in the URL. Refresh the status query so the indicator updates, and clear
  // the params so a reload doesn't replay the banner.
  useEffect(() => {
    const result = searchParams.get('jira');
    if (!result) return;
    if (result === 'connected') {
      qc.invalidateQueries({ queryKey: ['jira', 'status', orgId] });
    }
    const reason = searchParams.get('jira_reason');
    if (result === 'error' && reason) {
      setActionError(new Error(`Jira connection failed: ${reason}`));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('jira');
    next.delete('jira_reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, qc, orgId]);

  const connectMutation = useMutation({
    mutationFn: () => jiraApi.authorizeUrl(orgId),
    onSuccess: (data) => {
      // Hard navigation — Atlassian must own the tab for consent to render.
      window.location.href = data.url;
    },
    onError: (err) => setActionError(err),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => jiraApi.disconnect(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jira', 'status', orgId] });
      setActionError(null);
    },
    onError: (err) => setActionError(err),
  });

  const status = statusQuery.data;

  return (
    <section className="card">
      <h3>
        Jira integration{' '}
        {status?.connected ? (
          <span className="badge badge-good" style={{ marginLeft: '0.5rem' }}>Connected</span>
        ) : (
          <span className="badge badge-muted" style={{ marginLeft: '0.5rem' }}>Not connected</span>
        )}
      </h3>
      <p className="muted">
        Auto-detect Jira issue keys in PR titles, sync logged worklog time into the PR's actual
        hours, and feed Jira issue details into the AI estimate.
      </p>

      {statusQuery.isLoading ? <Spinner label="Loading Jira status…" /> : null}
      {statusQuery.error ? <ErrorBanner error={statusQuery.error} /> : null}
      {actionError ? <ErrorBanner error={actionError} /> : null}

      {status?.connected ? (
        <>
          <dl className="kv-list">
            <dt>Site</dt>
            <dd>
              {status.siteName ?? '—'}{' '}
              {status.siteUrl ? (
                <a className="link" href={status.siteUrl} target="_blank" rel="noreferrer">
                  {status.siteUrl} ↗
                </a>
              ) : null}
            </dd>
            <dt>Connected</dt>
            <dd>{status.connectedAt ? formatDateTime(status.connectedAt) : '—'}</dd>
            <dt>Scopes</dt>
            <dd className="muted small">{status.scopes ?? '—'}</dd>
          </dl>

          {status.webhookUrl ? (
            <div style={{ marginTop: '1rem' }}>
              <h4>Webhook URL</h4>
              <p className="muted small">
                Paste this into{' '}
                {status.siteUrl ? (
                  <a
                    className="link"
                    href={`${status.siteUrl.replace(/\/$/, '')}/plugins/servlet/webhooks`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Jira → Settings → System → WebHooks ↗
                  </a>
                ) : (
                  'Jira → Settings → System → WebHooks'
                )}{' '}
                (events: <code>worklog_created</code>, <code>worklog_updated</code>,{' '}
                <code>worklog_deleted</code>). It contains a per-org secret — keep it private;
                rotating it requires disconnecting and reconnecting.
              </p>
              <input
                className="input"
                readOnly
                value={status.webhookUrl}
                onFocus={(e) => e.currentTarget.select()}
                style={{ width: '100%', fontFamily: 'monospace' }}
              />
            </div>
          ) : null}

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
            <button
              className="btn btn-ghost btn-danger"
              onClick={async () => {
                const ok = await confirm({
                  title: 'Disconnect Jira',
                  message: 'Disconnect Jira? Linked issues stay attached but worklog sync stops.',
                  confirmLabel: 'Disconnect',
                  danger: true,
                });
                if (ok) disconnectMutation.mutate();
              }}
              disabled={disconnectMutation.isPending}
            >
              {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect Jira'}
            </button>
          </div>
        </>
      ) : (
        <button
          className="btn btn-primary"
          onClick={() => connectMutation.mutate()}
          disabled={connectMutation.isPending}
        >
          {connectMutation.isPending ? 'Redirecting…' : 'Connect to Jira'}
        </button>
      )}
    </section>
  );
}
